import { ui } from "./styles.js";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  CodeResult,
  EmptyState,
  Icon,
  ListRow,
  PaneResizer,
  SelectField,
  Status,
  useStoredPaneSize,
} from "@snowiespace/ui/web";
import { client } from "./api.js";
import { AppRuntimeView } from "./app-runtime-view.js";
import { PokeWorkspace } from "./poke.js";
import { Field, OperationError, object, type JsonObject } from "./shared.js";
import {
  codeAppLanguage,
  codeAppLocalIssue,
  codeAppManifest,
  codeAppTables,
  isCodeAppSource,
  parseCodeAppProject,
  personalizeWorkbenchStarter,
  serializeCodeAppProject,
  updateCodeAppFile,
} from "./workbench-code-project.js";

interface AppSummary {
  id: string;
  name: string;
  status: string;
  revision: number;
  packageDigest?: string;
  updatedAt: string;
}

interface AppValidation {
  sourceDigest: string;
  packageDigest: string;
  desktop1440x900: string;
  compact900x700: string;
  mobile390x844: string;
  behavior: string;
  accessibility: string;
  manifest: string;
  security: string;
  receipts: Array<{ validator: string; state: string; details: string }>;
  createdAt: string;
}

interface AppWorkspace extends AppSummary {
  concepts: Array<{ revision: number; digest: string; brief: JsonObject; createdAt: string }>;
  source?: { revision: number; contentTag: string; digest: string; source: string; dirty: boolean };
  validation?: AppValidation;
  candidateId?: string;
}

interface PackageSummary {
  moduleId: string;
  version: string;
  digest: string;
  title: string;
  status: string;
  dataPlane: "installation_sqlite" | "space_sqlite";
  starter?: boolean;
  framework?: string;
  starterSummary?: string;
  starterSource?: string;
  examples?: string[];
  authoringGuide?: string;
  sourceExamples?: string[];
}

type WorkbenchView = "preview" | "code" | "data" | "versions";

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function operation(configuration: JsonObject, name: string): string {
  return text(configuration[name]);
}

function exactValidationPassed(validation?: AppValidation): boolean {
  if (!validation) return false;
  const states = [
    validation.desktop1440x900,
    validation.compact900x700,
    validation.mobile390x844,
    validation.behavior,
    validation.accessibility,
    validation.manifest,
    validation.security,
  ];
  return states.every((state) => state === "passed")
    && validation.receipts.every((receipt) => receipt.state !== "failed")
    && validation.receipts.some((receipt) => ["workbench.source", "source.binding"].includes(receipt.validator) && receipt.state === "passed");
}

function starterForPurpose(source: string, name: string, purpose: string): string {
  return personalizeWorkbenchStarter(source, name, purpose);
}

function localSourceIssue(source: string, framework?: string): string | undefined {
  if (framework === "snowiespace-code-app-v1") return codeAppLocalIssue(source);
  let value: unknown;
  try {
    value = JSON.parse(source);
  } catch {
    return "The source is not valid JSON yet.";
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return "The source must be one JSON object.";
  const app = value as Record<string, unknown>;
  if (app.format !== "snowiespace-app-source-v2" || app.framework !== "snowiespace-app-v2") return "This signed framework requires source format v2 and framework snowiespace-app-v2.";
  if (app.data !== "shared-space-sqlite") return "Persistent Apps must use shared-space-sqlite.";
  const modes = Number(Boolean(app.collection && typeof app.collection === "object" && !Array.isArray(app.collection)))
    + Number(Boolean(app.selector && typeof app.selector === "object" && !Array.isArray(app.selector)));
  if (modes !== 1) return "Choose exactly one implemented App mode: collection or selector.";
  return undefined;
}

function appListState(app: AppSummary, released: boolean, candidateState?: string): string {
  if (released || candidateState === "active") return "released";
  if (candidateState === "preview_ready" || candidateState === "approved") return "Trial ready";
  if (candidateState === "failed") return "Trial failed";
  if (candidateState === "superseded") return "Trial replaced";
  if (candidateState === "loading") return "checking Trial";
  if (app.status === "published") return "Trial unavailable";
  return app.status.replaceAll("_", " ");
}

function AppList({ apps, releasedAppIds, candidateStates, selected, onSelect, onCreate }: {
  apps: AppSummary[];
  releasedAppIds: ReadonlySet<string>;
  candidateStates: ReadonlyMap<string, string>;
  selected?: string;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  return <section className={ui("app-list")} data-scroll-owner="app-list">
    <header>
      <div><span className={ui("ss-eyebrow")}>Improve this Space</span><h1>Workbench</h1></div>
      <Button icon="plus" onPress={onCreate}>New App</Button>
    </header>
    <p className={ui("body-copy")}>Create a working Trial, shape it with Snowie, then release that exact version into this Space.</p>
    <div className={ui("app-list-heading")}><strong>Your Apps</strong><Badge>{apps.length}</Badge></div>
    {apps.length
      ? <div className={ui("resource-items")}>{apps.map((app) => <Button
          key={app.id}
          variant="quiet"
          className={ui("resource-item", selected === app.id && "resource-item--selected")}
          onPress={() => onSelect(app.id)}
        ><span>{app.name}</span><small>{appListState(app, releasedAppIds.has(app.id), candidateStates.get(app.id))} · revision {app.revision}</small></Button>)}</div>
      : <EmptyState icon="workbench" title="No Apps yet">Start with a name and one purpose. Workbench creates the first safe Trial.</EmptyState>}
  </section>;
}

function WorkbenchTabs({ value, onChange }: { value: WorkbenchView; onChange: (value: WorkbenchView) => void }) {
  return <nav className={ui("workbench-tabs")} aria-label="App editor views">
    {(["preview", "code", "data", "versions"] as const).map((view) => <Button
      key={view}
      variant="quiet"
      className={ui("workbench-tab", value === view && "workbench-tab--active")}
      onPress={() => onChange(view)}
    >{view.charAt(0).toUpperCase() + view.slice(1)}</Button>)}
  </nav>;
}

function ReleaseSummary({ candidate, isPending, onRelease }: {
  candidate?: Awaited<ReturnType<typeof client.compositionCandidate>>;
  isPending: boolean;
  onRelease: () => void;
}) {
  if (!candidate || !["preview_ready", "approved"].includes(candidate.state)) return null;
  return <div className={ui("trial-release-bar")}>
    <span><strong>Trial {candidate.state === "approved" ? "approved" : "ready"}</strong><small>Release binds this exact digest and keeps the previous composition available for rollback.</small></span>
    <Button isDisabled={isPending} onPress={onRelease}>Release App</Button>
  </div>;
}

export function CandidateReview({ spaceId, candidateId, onActivated }: { spaceId: string; candidateId?: string; onActivated: () => void }) {
  const queryClient = useQueryClient();
  const candidate = useQuery({ queryKey: ["composition", "candidate", spaceId, candidateId], queryFn: () => client.compositionCandidate(spaceId, candidateId!), enabled: Boolean(candidateId) });
  const approve = useMutation({ mutationFn: () => client.approveCompositionCandidate(spaceId, candidateId!, { digest: candidate.data!.digest, approveGrantWidening: candidate.data!.changes.grantsAdded.length > 0 }, crypto.randomUUID()), onSuccess: () => queryClient.invalidateQueries({ queryKey: ["composition"] }) });
  const activate = useMutation({ mutationFn: () => client.activateCompositionCandidate(spaceId, candidateId!, candidate.data!.digest, crypto.randomUUID()), onSuccess: async () => { await Promise.all([queryClient.invalidateQueries({ queryKey: ["composition"] }), queryClient.invalidateQueries({ queryKey: ["bootstrap", spaceId] })]); onActivated(); } });
  if (!candidateId) return <EmptyState title="No candidate selected">Choose exact package changes first.</EmptyState>;
  if (!candidate.data) return <><EmptyState title="Opening candidate">Reading kernel validation evidence…</EmptyState><OperationError error={candidate.error} /></>;
  const value = candidate.data;
  return <section className={ui("trial-review")} data-scroll-owner="candidate-review">
    <header><div><span className={ui("ss-eyebrow")}>Kernel candidate</span><h3>Review exact composition</h3></div><Status tone={value.state === "failed" ? "warning" : value.state === "active" ? "positive" : "neutral"}>{value.state.replaceAll("_", " ")}</Status></header>
    <CodeResult value={{ changes: value.changes, evidence: value.evidence, digest: value.digest }} />
    <div className={ui("action-row")}><Button variant="secondary" isDisabled={value.state !== "preview_ready" || approve.isPending} onPress={() => approve.mutate()}>Approve exact candidate</Button><Button isDisabled={value.state !== "approved" || activate.isPending} onPress={() => activate.mutate()}>Activate</Button></div>
    <OperationError error={approve.error ?? activate.error} />
  </section>;
}

export function AppView({ spaceId, moduleId, title, configuration, initialAppId }: {
  spaceId: string;
  moduleId: string;
  title: string;
  configuration: JsonObject;
  initialAppId?: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const listOperation = operation(configuration, "listOperation");
  const appsQuery = useQuery({
    queryKey: ["apps", spaceId],
    queryFn: async () => object((await client.invoke(spaceId, listOperation, {}, false)).output).apps as AppSummary[],
    refetchInterval: 2_000,
  });
  const candidateApps = (appsQuery.data ?? []).filter((app) => app.status === "published");
  const listedAppQueries = useQueries({
    queries: candidateApps.map((app) => ({
      queryKey: ["app", spaceId, app.id],
      queryFn: async () => object((await client.invoke(spaceId, operation(configuration, "getOperation"), { appId: app.id }, false)).output) as unknown as AppWorkspace,
      refetchInterval: 2_000,
    })),
  });
  const listedCandidates = listedAppQueries.flatMap((query, index) => query.data?.candidateId ? [{ appId: candidateApps[index]!.id, candidateId: query.data.candidateId }] : []);
  const listedCandidateQueries = useQueries({
    queries: listedCandidates.map((item) => ({
      queryKey: ["composition", "candidate", spaceId, item.candidateId],
      queryFn: () => client.compositionCandidate(spaceId, item.candidateId),
      refetchInterval: 2_000,
    })),
  });
  const candidateStates = new Map<string, string>();
  for (const app of appsQuery.data ?? []) if (app.status === "published") candidateStates.set(app.id, "loading");
  listedCandidates.forEach((item, index) => candidateStates.set(item.appId, listedCandidateQueries[index]?.data?.state ?? "loading"));
  const composition = useQuery({ queryKey: ["composition", spaceId], queryFn: () => client.composition(spaceId) });
  const activeAppPackage = composition.data?.active.manifest.packages.find((item) => item.moduleId === moduleId);
  const appUpgradePackage = (composition.data?.availablePackages ?? [])
    .filter((item) => item.moduleId === moduleId && item.provenance === "first-party" && item.status === "ready")
    .sort((left, right) => right.version.localeCompare(left.version, undefined, { numeric: true }))[0];
  const appUpgradeRequired = Boolean(activeAppPackage && appUpgradePackage && activeAppPackage.digest !== appUpgradePackage.digest);
  const [upgradeCandidateId, setUpgradeCandidateId] = useState<string>();
  const packages = useMemo<PackageSummary[]>(() => (composition.data?.availablePackages ?? [])
    .filter((item) => item.provenance === "third-party")
    .map((item) => ({
      moduleId: item.moduleId,
      version: item.version,
      digest: item.digest,
      title: item.title,
      status: item.status,
      dataPlane: item.manifest.runtime.dataPlane ?? "installation_sqlite",
      starter: item.manifest.workbench?.starter,
      framework: item.manifest.workbench?.framework,
      starterSummary: item.manifest.workbench?.summary,
      starterSource: item.manifest.workbench?.source,
      examples: item.manifest.workbench?.examples,
      authoringGuide: item.manifest.workbench?.authoring?.guide,
      sourceExamples: item.manifest.workbench?.authoring?.sourceExamples,
    }))
    .sort((left, right) => right.version.localeCompare(left.version, undefined, { numeric: true }) || left.title.localeCompare(right.title)), [composition.data]);
  const starters = useMemo(() => packages.filter((item) => ["snowiespace-code-app-v1", "snowiespace-app-v2"].includes(item.framework ?? "") && item.starter && item.starterSource && item.status === "ready"), [packages]);
  const [selectedId, setSelectedId] = useState<string | undefined>(initialAppId);
  const [creating, setCreating] = useState(false);
  const [listRequested, setListRequested] = useState(false);
  const [view, setView] = useState<WorkbenchView>("preview");
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [starterDigest, setStarterDigest] = useState("");
  const [source, setSource] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedFile, setSelectedFile] = useState("app.js");
  const [dataBranch, setDataBranch] = useState<"trial" | "live">("trial");
  const [snowieWidth, setSnowieWidth] = useStoredPaneSize("workbench.app.snowie", 400, 340, 560);
  const appQuery = useQuery({
    queryKey: ["app", spaceId, selectedId],
    queryFn: async () => object((await client.invoke(spaceId, operation(configuration, "getOperation"), { appId: selectedId }, false)).output) as unknown as AppWorkspace,
    enabled: Boolean(selectedId),
    refetchInterval: 2_000,
  });

  useEffect(() => {
    if (!starterDigest && starters[0]) setStarterDigest(starters[0].digest);
  }, [starterDigest, starters]);
  useEffect(() => {
    const first = appsQuery.data?.find((app) => app.id === initialAppId) ?? appsQuery.data?.[0];
    if (!selectedId && !creating && !listRequested && first) setSelectedId(first.id);
  }, [appsQuery.data, creating, initialAppId, listRequested, selectedId]);
  useEffect(() => {
    if (initialAppId) {
      setCreating(false);
      setListRequested(false);
      setSelectedId(initialAppId);
    }
  }, [initialAppId]);
  useEffect(() => {
    if (appQuery.data?.source) setSource(appQuery.data.source.source);
  }, [appQuery.data?.source?.contentTag]);
  useEffect(() => {
    const current = appQuery.data?.packageDigest;
    const editableCurrent = packages.find((item) => item.digest === current && ["snowiespace-code-app-v1", "snowiespace-app-v2"].includes(item.framework ?? ""));
    if (editableCurrent) setSelectedPackage(editableCurrent.digest);
    else if (starters[0]) setSelectedPackage(starters[0].digest);
  }, [appQuery.data?.id, appQuery.data?.packageDigest, packages, starters]);

  const invalidate = async (appId = selectedId) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["apps", spaceId] }),
      queryClient.invalidateQueries({ queryKey: ["app", spaceId, appId] }),
      queryClient.invalidateQueries({ queryKey: ["composition"] }),
      queryClient.invalidateQueries({ queryKey: ["bootstrap", spaceId] }),
    ]);
  };

  const prepareWorkbenchUpgrade = useMutation({
    mutationFn: () => client.createCompositionCandidate(spaceId, {
      intent: `Update this Space to Workbench App ${appUpgradePackage!.version} while preserving its existing Apps and data.`,
      packageDigests: [appUpgradePackage!.digest],
      removeModuleIds: [],
    }, crypto.randomUUID()),
    onSuccess: (candidate) => setUpgradeCandidateId(candidate.id),
  });

  const selectedPackageDefinition = packages.find((item) => item.digest === selectedPackage);
  const selectedFrameworkConflict = selectedPackageDefinition
    ? composition.data?.active.manifest.appReleases.find((release) => {
        if (release.packageDigest === selectedPackage) return false;
        const boundPackage = composition.data?.active.manifest.packages.find((item) => item.digest === release.packageDigest);
        return boundPackage?.moduleId === selectedPackageDefinition.moduleId;
      })
    : undefined;

  const create = useMutation({
    mutationFn: async () => {
      const starter = starters.find((item) => item.digest === starterDigest);
      if (!starter?.starterSource) throw new Error("The editable Workbench framework is not ready.");
      const flowId = crypto.randomUUID();
      let current: AppWorkspace | undefined;
      const brief = {
        problem: purpose.trim(),
        currentPain: purpose.trim(),
        affectedPeople: ["People in this Space"],
        useCases: [purpose.trim()],
        desiredOutcome: purpose.trim(),
        successSignals: ["The App is useful for its stated purpose"],
        requirements: [],
        assumptions: [],
        constraints: [],
        nonGoals: [],
        openQuestions: [],
      };
      try {
        current = object((await client.invoke(spaceId, operation(configuration, "createOperation"), { name: name.trim(), brief }, true, `${flowId}:create`)).output) as unknown as AppWorkspace;
        const concept = current.concepts.at(-1);
        if (!concept) throw new Error("The App concept was not created.");
        current = object((await client.invoke(spaceId, operation(configuration, "conceptApproveOperation"), { appId: current.id, expectedRevision: current.revision, conceptDigest: concept.digest }, true, `${flowId}:approve`)).output) as unknown as AppWorkspace;
        if (!current.source) throw new Error("The App source was not materialized.");
        const starterSource = starterForPurpose(starter.starterSource, name, purpose);
        current = object((await client.invoke(spaceId, operation(configuration, "sourcePutOperation"), {
          appId: current.id,
          expectedRevision: current.revision,
          expectedContentTag: current.source.contentTag,
          source: starterSource,
        }, true, `${flowId}:source`)).output) as unknown as AppWorkspace;
        current = object((await client.invoke(spaceId, operation(configuration, "validateOperation"), {
          appId: current.id,
          expectedRevision: current.revision,
          packageDigest: starter.digest,
        }, true, `${flowId}:validate`)).output) as unknown as AppWorkspace;
        if (!exactValidationPassed(current.validation)) throw new Error("The framework rejected the first App source. Open its receipts for the exact reason.");
        return await client.invoke(spaceId, operation(configuration, "publishOperation"), {
          appId: current.id,
          expectedRevision: current.revision,
          packageDigest: starter.digest,
          intent: `Create the first isolated Trial for ${current.name}.`,
        }, true, `${flowId}:trial`);
      } catch (error) {
        if (current) {
          setSelectedId(current.id);
          setCreating(false);
          setView("code");
          await invalidate(current.id);
        }
        throw error;
      }
    },
    onSuccess: async (result) => {
      const app = object(result.output) as unknown as AppWorkspace;
      setSelectedId(app.id);
      setCreating(false);
      setView("preview");
      await invalidate(app.id);
    },
  });

  const updateTrial = useMutation({
    mutationFn: async () => {
      if (selectedFrameworkConflict) {
        throw new Error("This legacy framework would replace " + selectedFrameworkConflict.name + "'s exact runtime. Choose the newest co-installable Workbench framework.");
      }
      let current = appQuery.data!;
      if (!current.source) throw new Error("This App has no editable source.");
      const flowId = crypto.randomUUID();
      if (source !== current.source.source) {
        current = object((await client.invoke(spaceId, operation(configuration, "sourcePutOperation"), {
          appId: current.id,
          expectedRevision: current.revision,
          expectedContentTag: current.source.contentTag,
          source,
        }, true, `${flowId}:source`)).output) as unknown as AppWorkspace;
      }
      const validation = current.validation;
      const alreadyValidated = Boolean(validation
        && validation.sourceDigest === current.source?.digest
        && validation.packageDigest === selectedPackage
        && exactValidationPassed(validation));
      if (!alreadyValidated) {
        current = object((await client.invoke(spaceId, operation(configuration, "validateOperation"), {
          appId: current.id,
          expectedRevision: current.revision,
          packageDigest: selectedPackage,
        }, true, `${flowId}:validate`)).output) as unknown as AppWorkspace;
      }
      if (!exactValidationPassed(current.validation)) throw new Error("Validation failed. The exact receipt is available below the code editor.");
      return client.invoke(spaceId, operation(configuration, "publishOperation"), {
        appId: current.id,
        expectedRevision: current.revision,
        packageDigest: selectedPackage,
        intent: `Update the isolated Trial for ${current.name}.`,
      }, true, `${flowId}:trial`);
    },
    onSuccess: async () => {
      setView("preview");
      await invalidate();
    },
  });

  const activeApp = appQuery.data;
  const candidateQuery = useQuery({
    queryKey: ["composition", "candidate", spaceId, activeApp?.candidateId],
    queryFn: () => client.compositionCandidate(spaceId, activeApp!.candidateId!),
    enabled: Boolean(activeApp?.candidateId),
    refetchInterval: 2_000,
  });
  const candidateOpen = candidateQuery.data && ["preview_ready", "approved"].includes(candidateQuery.data.state);
  const trialRelease = candidateOpen ? candidateQuery.data?.proposedManifest.appReleases.find((item) => item.appId === activeApp?.id) : undefined;
  const trialPackage = trialRelease ? candidateQuery.data?.proposedManifest.packages.find((item) => item.digest === trialRelease.packageDigest) : undefined;
  const trialView = trialPackage?.manifest.views.find((item) => item.kind === "app_runtime");
  const trialOperations = trialPackage?.manifest.operations.map((item) => ({ id: item.id, moduleId: item.moduleId, mutation: item.mutation }));
  const liveRelease = composition.data?.active.manifest.appReleases.find((item) => item.appId === activeApp?.id);
  const livePackage = liveRelease ? composition.data?.active.manifest.packages.find((item) => item.digest === liveRelease.packageDigest) : undefined;
  const liveView = livePackage?.manifest.views.find((item) => item.kind === "app_runtime");
  const selectedPackageSummary = selectedPackageDefinition;
  const selectedPackageRef = (composition.data?.availablePackages ?? []).find((item) => item.digest === selectedPackage);
  const selectedStarter = starters.find((item) => item.digest === selectedPackage);
  const codeStarter = starters.find((item) => item.framework === "snowiespace-code-app-v1");
  const parsedCodeSource = useMemo(() => parseCodeAppProject(source), [source]);
  const codeProject = parsedCodeSource.project;
  const codeManifest = codeAppManifest(codeProject);
  const tables = codeAppTables(codeProject);
  const selectedCodeFile = codeProject?.files.find((file) => file.path === selectedFile);
  useEffect(() => {
    if (codeProject && !codeProject.files.some((file) => file.path === selectedFile)) setSelectedFile(codeProject.files.find((file) => file.path === "app.js")?.path ?? codeProject.files[0]!.path);
  }, [codeProject, selectedFile]);
  const sourceIssue = source ? localSourceIssue(source, selectedPackageDefinition?.framework) : "This App has no source yet.";
  const sourceReceipt = activeApp?.validation?.receipts.find((receipt) => receipt.validator === "workbench.source");
  const trialMatchesDraft = Boolean(activeApp?.source && trialRelease
    && trialRelease.sourceDigest === activeApp.source.digest
    && trialRelease.packageDigest === selectedPackage
    && source === activeApp.source.source);
  const draftValidationPassed = Boolean(activeApp?.source
    && source === activeApp.source.source
    && activeApp.validation?.sourceDigest === activeApp.source.digest
    && activeApp.validation.packageDigest === selectedPackage
    && exactValidationPassed(activeApp.validation));
  const draftValidationLabel = draftValidationPassed
    ? "Exact validation passed"
    : activeApp?.validation
      && activeApp.validation.sourceDigest === activeApp.source?.digest
      && activeApp.validation.packageDigest === selectedPackage
      && source === activeApp.source?.source
      ? "Validation failed"
      : activeApp?.validation
        ? "Validation required"
        : "Not validated";
  const trialCodeAvailable = Boolean(candidateOpen && trialPackage?.manifest.workbench?.framework === "snowiespace-code-app-v1");
  const liveCodeAvailable = livePackage?.manifest.workbench?.framework === "snowiespace-code-app-v1";
  const tableQueries = useQueries({
    queries: tables.map((table) => ({
      queryKey: ["code-app-data", spaceId, activeApp?.id, dataBranch, candidateQuery.data?.id, table.id],
      queryFn: async () => {
        const result = dataBranch === "trial"
          ? await client.invokeTrial(spaceId, candidateQuery.data!.id, "code.data.list", { table: table.id }, false)
          : await client.invokeApp(spaceId, activeApp!.id, "code.data.list", { table: table.id }, false);
        return object(result.output) as { table: string; records: unknown[] };
      },
      enabled: view === "data" && Boolean(activeApp) && (dataBranch === "trial" ? trialCodeAvailable : liveCodeAvailable),
    })),
  });

  const loadCodeStarter = (starter: PackageSummary, preferMythic: boolean) => {
    const appPurpose = text(activeApp?.concepts.at(-1)?.brief.desiredOutcome) || text(activeApp?.concepts.at(-1)?.brief.problem) || purpose;
    const candidates = [starter.starterSource, ...(starter.sourceExamples ?? [])].filter((item): item is string => Boolean(item) && isCodeAppSource(item!));
    const selected = preferMythic
      ? candidates.find((candidate) => codeAppManifest(parseCodeAppProject(candidate).project)?.name.toLowerCase().includes("mythic")) ?? candidates[0]
      : candidates[0];
    if (!selected) return;
    setSelectedPackage(starter.digest);
    setSource(starterForPurpose(selected, activeApp?.name ?? name, appPurpose));
    setSelectedFile("app.js");
  };

  const releaseApp = useMutation({
    mutationFn: async () => {
      const candidate = candidateQuery.data!;
      if (candidate.state === "preview_ready") {
        await client.approveCompositionCandidate(spaceId, candidate.id, {
          digest: candidate.digest,
          approveGrantWidening: candidate.changes.grantsAdded.length > 0,
        }, crypto.randomUUID());
      }
      return client.activateCompositionCandidate(spaceId, candidate.id, candidate.digest, crypto.randomUUID());
    },
    onSuccess: async () => invalidate(),
  });
  const rollbackPreview = useMutation({
    mutationFn: () => client.previewCompositionRollback(spaceId, composition.data!.previous!.id),
  });
  const rollback = useMutation({
    mutationFn: () => client.commitCompositionRollback(spaceId, {
      token: rollbackPreview.data!.token,
      targetDigest: rollbackPreview.data!.targetDigest,
    }, crypto.randomUUID()),
    onSuccess: async () => {
      await invalidate();
      rollbackPreview.reset();
      setView("preview");
    },
  });

  const renderPreview = () => {
    if (candidateOpen && trialRelease && trialPackage && trialView?.asset) {
      return <AppRuntimeView
        spaceId={spaceId}
        appId={activeApp?.id}
        source={trialRelease.source}
        sourceDigest={trialRelease.sourceDigest}
        moduleId={trialPackage.moduleId}
        title={activeApp?.name ?? trialPackage.title}
        version={trialPackage.version}
        asset={trialView.asset}
        candidateId={candidateQuery.data?.id}
        operations={trialOperations}
      />;
    }
    if (liveRelease && livePackage && liveView?.asset) {
      return <AppRuntimeView
        spaceId={spaceId}
        appId={activeApp?.id}
        source={liveRelease.source}
        sourceDigest={liveRelease.sourceDigest}
        moduleId={livePackage.moduleId}
        title={activeApp?.name ?? liveRelease.name}
        version={livePackage.version}
        asset={liveView.asset}
      />;
    }
    if (candidateQuery.data?.state === "superseded") {
      return <EmptyState icon="history" title="This Trial was replaced">The draft is safe. Choose Create Trial to reopen this exact version without changing Live.</EmptyState>;
    }
    if (candidateQuery.data?.state === "failed") {
      const releaseFailed = candidateQuery.data.failureCode?.startsWith("ACTIVATION_");
      return <EmptyState icon="evidence" title={releaseFailed ? "The release attempt stopped" : "This Trial did not validate"}>
        {releaseFailed
          ? "Live was not changed. Choose Create Trial to reopen this exact version, test it, and release with a fresh attempt."
          : "Open Code for the exact framework receipt, correct the source, then create a new Trial."}
      </EmptyState>;
    }
    return <EmptyState icon="evidence" title="No working Trial yet">Open Code and choose Update Trial. Live remains unchanged while you test.</EmptyState>;
  };

  const operationError = create.error ?? updateTrial.error ?? releaseApp.error;
  return <div className={ui("app-layout", (selectedId || creating) && "app-layout--focused")}>
    <AppList
      apps={appsQuery.data ?? []}
      releasedAppIds={new Set(composition.data?.active.manifest.appReleases.map((release) => release.appId) ?? [])}
      candidateStates={candidateStates}
      selected={selectedId}
      onCreate={() => { setCreating(true); setListRequested(false); setSelectedId(undefined); setView("preview"); }}
      onSelect={(id) => { setCreating(false); setListRequested(false); setSelectedId(id); setView("preview"); }}
    />
    <section className={ui("app-workbench", activeApp && "app-workbench--editor")} data-scroll-owner="app-workbench">
      <Button className={ui("app-mobile-back")} icon="arrow-left" variant="quiet" onPress={() => {
        setCreating(false);
        setListRequested(true);
        setSelectedId(undefined);
        void navigate({ to: "/spaces/$spaceId/modules/$moduleId", params: { spaceId, moduleId }, search: { appId: undefined } });
      }}>Workbench</Button>
      {appUpgradeRequired
        ? <section className={ui("page")} data-scroll-owner="workbench-upgrade">
            <header className={ui("page-heading")}><span className={ui("ss-eyebrow")}>One-time Space update</span><h1>Finish setting up the new Workbench</h1><p>This Space still uses App {activeAppPackage?.version}. App {appUpgradePackage?.version} adds the selected signed framework’s authoring contract to each App Snowie snapshot while preserving existing App records and data.</p></header>
            <Card title="Member-controlled package update" eyebrow="Kernel boundary">
              <p className={ui("body-copy")}>Preparing creates a reversible exact-digest candidate. Approval and activation remain separate, and other Spaces are not changed.</p>
              <Button isDisabled={prepareWorkbenchUpgrade.isPending || Boolean(upgradeCandidateId)} onPress={() => prepareWorkbenchUpgrade.mutate()}>{prepareWorkbenchUpgrade.isPending ? "Preparing…" : `Prepare Workbench ${appUpgradePackage?.version}`}</Button>
              <OperationError error={composition.error ?? prepareWorkbenchUpgrade.error} />
              {upgradeCandidateId ? <CandidateReview spaceId={spaceId} candidateId={upgradeCandidateId} onActivated={async () => { setUpgradeCandidateId(undefined); await invalidate(); }} /> : null}
            </Card>
          </section>
        : creating
        ? <div className={ui("new-app-flow")}>
            <header className={ui("workbench-heading")}>
              <span className={ui("ss-eyebrow")}>New App · one short wizard</span>
              <h2>What should this Space help you do?</h2>
              <p>Workbench creates a real HTML, CSS, JavaScript, action, and schema project, then opens its isolated Trial beside Snowie.</p>
            </header>
            <div className={ui("wizard-steps")}><Badge>1 · Purpose</Badge><span /><Badge>2 · Working Trial</Badge></div>
            <Field label="App name" value={name} onChange={setName} placeholder="Garden Companion" />
            <Field label="Purpose" value={purpose} onChange={setPurpose} textarea placeholder="Help us track planting, care, and harvest notes." />
            {starters[0]?.examples?.length ? <div className={ui("starter-examples")}><span>Examples</span>{starters[0].examples.map((example) => <Button key={example} variant="quiet" onPress={() => {
              if (!name.trim()) setName(example);
              setPurpose(`Help this Space use ${example.toLowerCase()} as a practical, persistent App.`);
            }}>{example}</Button>)}</div> : null}
            <div className={ui("kernel-note")}><Icon name="receipt" /><span><strong>Real code, contained by default.</strong><small>Your files own the UI and custom logic. Data lives in this Space’s shared SQLite; web/search and Snowie use kernel capabilities, never raw infrastructure.</small></span></div>
            <Button isDisabled={create.isPending || !name.trim() || !purpose.trim() || !starterDigest} onPress={() => create.mutate()}>{create.isPending ? "Creating Trial…" : "Create Trial"}</Button>
            <OperationError error={create.error ?? composition.error ?? appsQuery.error} />
          </div>
        : activeApp
          ? <>
              <header className={ui("workbench-heading", "workbench-heading--app")}>
                <div><span className={ui("ss-eyebrow")}>{title} · App editor</span><h2>{activeApp.name}</h2><p>{text(activeApp.concepts.at(-1)?.brief.desiredOutcome) || text(activeApp.concepts.at(-1)?.brief.problem)}</p></div>
                <Status tone={candidateOpen ? "warning" : liveRelease ? "positive" : candidateQuery.data?.state === "failed" ? "warning" : "neutral"}>{candidateOpen ? "Trial running" : liveRelease ? "Released" : candidateQuery.data?.state === "failed" ? "Trial failed" : candidateQuery.data?.state === "superseded" ? "Trial replaced" : "Draft"}</Status>
              </header>
              <div className={ui("app-editor-shell")} style={{ "--app-snowie-width": `${snowieWidth}px` } as CSSProperties}>
                <main className={ui("app-editor-main")}>
                  <div className={ui("app-editor-toolbar")}><WorkbenchTabs value={view} onChange={setView} /><Button
                    variant="secondary"
                    isDisabled={updateTrial.isPending || !selectedPackage || !activeApp.source || trialMatchesDraft || Boolean(sourceIssue) || Boolean(selectedFrameworkConflict)}
                    onPress={() => updateTrial.mutate()}
                  >{updateTrial.isPending ? "Updating…" : candidateOpen ? "Update Trial" : "Create Trial"}</Button></div>
                  {view === "preview" ? <div className={ui("app-preview-view")} data-scroll-owner="app-preview">{renderPreview()}<ReleaseSummary candidate={candidateQuery.data} isPending={releaseApp.isPending} onRelease={() => releaseApp.mutate()} /></div> : null}
                  {view === "code" ? <div className={ui("app-code-view", codeProject && "app-code-view--project")} data-scroll-owner="app-code">
                    <SelectField
                      label="App framework"
                      value={selectedPackage}
                      options={starters.map((item) => ({ id: item.digest, label: `${item.title} ${item.version} · ${item.framework}` }))}
                      onChange={setSelectedPackage}
                    />
                    {selectedFrameworkConflict ? <div className={ui("danger-panel")}><strong>Choose a co-installable framework</strong><p>This legacy version would replace {selectedFrameworkConflict.name}’s exact runtime in this Space. Workbench 1.4 and later can run beside existing Apps and Live versions.</p></div> : null}
                    {!codeProject && codeStarter ? <section className={ui("code-upgrade-banner")}>
                      <span><Icon name="code" /></span>
                      <div><strong>Make this a real code-backed App</strong><p>Convert the draft into editable files. The current Live release stays available until you create, test, and explicitly release the new Trial.</p></div>
                      <Button onPress={() => loadCodeStarter(codeStarter, /mythic|wow|composition/i.test(`${activeApp.name} ${text(activeApp.concepts.at(-1)?.brief.problem)}`))}>Convert to code project</Button>
                    </section> : null}
                    {selectedPackageDefinition?.framework === "snowiespace-code-app-v1" && codeProject ? <>
                      <div className={ui("code-project-bar")}>
                        <div><span className={ui("ss-eyebrow")}>Code project</span><strong>{codeManifest?.name ?? activeApp.name} · {codeManifest?.version ?? "Version needs attention"}</strong><small>{codeProject.files.length} files · custom UI and actions · shared Space SQLite</small></div>
                        <div className={ui("action-row")}><Button variant="secondary" isDisabled={!selectedStarter} onPress={() => selectedStarter && loadCodeStarter(selectedStarter, false)}>Reset to starter</Button><Button variant="secondary" isDisabled={!selectedStarter} onPress={() => selectedStarter && loadCodeStarter(selectedStarter, true)}>Load Mythic+ example</Button></div>
                      </div>
                      <div className={ui("code-workspace")}>
                        <aside className={ui("code-file-tree")} data-scroll-owner="app-code-files" aria-label="App project files">
                          <header><span className={ui("ss-eyebrow")}>Project</span><small>Exact source revision</small></header>
                          {codeProject.files.map((file) => <Button key={file.path} variant="quiet" className={ui("code-file", selectedFile === file.path && "code-file--selected")} onPress={() => setSelectedFile(file.path)}><Icon name={file.path.endsWith(".json") ? "receipt" : "code"} /><span>{file.path}</span>{activeApp.source?.source !== source && selectedFile === file.path ? <i aria-label="Unsaved project">•</i> : null}</Button>)}
                        </aside>
                        <section className={ui("code-editor-pane")}>
                          <header><div><strong>{selectedCodeFile?.path ?? "Choose a file"}</strong><small>{selectedCodeFile ? codeAppLanguage(selectedCodeFile.path) : "Project file"}</small></div>{selectedCodeFile?.path.endsWith(".json") ? <Button variant="quiet" onPress={() => {
                            try {
                              const formatted = `${JSON.stringify(JSON.parse(selectedCodeFile.content), null, 2)}\n`;
                              setSource(serializeCodeAppProject(updateCodeAppFile(codeProject, selectedCodeFile.path, formatted)));
                            } catch { return; }
                          }}>Format</Button> : null}</header>
                          {selectedCodeFile ? <textarea aria-label={`Edit ${selectedCodeFile.path}`} value={selectedCodeFile.content} spellCheck={false} onChange={(event) => setSource(serializeCodeAppProject(updateCodeAppFile(codeProject, selectedCodeFile.path, event.target.value)))} /> : <EmptyState title="Choose a project file">Select a file from the project tree.</EmptyState>}
                          <footer><span><Icon name="snowie" /> Snowie sees this file, the project tree, exact source tag, and framework guide.</span><Status tone={sourceIssue ? "warning" : "positive"}>{sourceIssue ? "Needs attention" : "Project valid locally"}</Status></footer>
                        </section>
                      </div>
                    </> : <>
                      <div className={ui("kernel-note")}><Icon name="workbench" /><span><strong>Legacy declarative framework.</strong><small>This exact release remains supported. Choose Convert to code project to own the UI and custom logic as files.</small></span></div>
                      <div className={ui("action-row")}><Button variant="secondary" isDisabled={!selectedStarter?.starterSource} onPress={() => selectedStarter?.starterSource && setSource(starterForPurpose(selectedStarter.starterSource, activeApp.name, text(activeApp.concepts.at(-1)?.brief.desiredOutcome) || text(activeApp.concepts.at(-1)?.brief.problem)))}>Load legacy starter</Button><Button variant="quiet" onPress={() => { try { setSource(`${JSON.stringify(JSON.parse(source), null, 2)}\n`); } catch { return; } }}>Format JSON</Button></div>
                      <Field label="Legacy declarative source" value={source} onChange={setSource} textarea />
                    </>}
                    {selectedStarter?.authoringGuide ? <details><summary>Signed framework authoring contract</summary><pre tabIndex={0}>{selectedStarter.authoringGuide}</pre></details> : <div className={ui("danger-panel")}><strong>Snowie authoring guide unavailable</strong><p>This framework can run the App, but it does not publish an exact source guide for App Snowie.</p></div>}
                    {sourceIssue ? <div className={ui("danger-panel")}><strong>Source needs attention</strong><p>{sourceIssue}</p></div> : null}
                    {sourceReceipt?.state === "failed" && source === activeApp.source?.source ? <div className={ui("danger-panel")}><strong>Framework validation failed</strong><p>{sourceReceipt.details}</p></div> : null}
                    <div className={ui("code-status-row")}><code>{activeApp.source?.contentTag ?? "No source"}</code><Status tone={draftValidationPassed ? "positive" : activeApp.validation ? "warning" : "neutral"}>{draftValidationLabel}</Status></div>
                    {activeApp.validation ? <details><summary>Validation receipts</summary><CodeResult value={activeApp.validation} /></details> : null}
                  </div> : null}
                  {view === "data" ? <div className={ui("app-data-view")} data-scroll-owner="app-data">
                    <header className={ui("data-view-heading")}><div><span className={ui("ss-eyebrow")}>Shared Space SQLite</span><h3>App data</h3><p>Inspect the declared tables for one exact App and branch. Raw SQL and other Apps stay kernel-only.</p></div><div className={ui("branch-switch")}><Button variant={dataBranch === "trial" ? "secondary" : "quiet"} isDisabled={!trialCodeAvailable} onPress={() => setDataBranch("trial")}>Trial</Button><Button variant={dataBranch === "live" ? "secondary" : "quiet"} isDisabled={!liveCodeAvailable} onPress={() => setDataBranch("live")}>Live</Button></div></header>
                    {codeProject ? <div className={ui("data-table-grid")}>
                      {tables.length ? tables.map((table, index) => <Card key={table.id} title={table.title} eyebrow={`${dataBranch === "trial" ? "Trial branch" : "Live branch"} · ${table.id}`}>
                        <p className={ui("body-copy")}>{table.description ?? "Project-declared App table."}</p>
                        <div className={ui("schema-fields")}>{table.fields.map((field) => <span key={field.id}><code>{field.id}</code><small>{field.type}{field.required ? " · required" : ""}</small></span>)}</div>
                        {dataBranch === "trial" ? !trialCodeAvailable ? <Status tone="neutral">Create a code Trial to inspect this branch</Status> : null : !liveCodeAvailable ? <Status tone="neutral">Release the code App to inspect Live</Status> : null}
                        {tableQueries[index]?.data ? <><div className={ui("data-row-count")}><strong>{tableQueries[index].data.records.length}</strong><span>visible records</span></div><CodeResult value={tableQueries[index].data.records} /></> : null}
                        <OperationError error={tableQueries[index]?.error} />
                      </Card>) : <EmptyState title="No declared tables">Add tables to schema.json. App actions can then persist bounded JSON rows.</EmptyState>}
                    </div> : <div className={ui("data-contract-grid")}>
                      <Card title="Live data" eyebrow="Shared Space SQLite"><ListRow icon="receipt" title="One private App namespace" description="Persistent across framework and source updates" meta={<Status tone={selectedPackageSummary?.dataPlane === "space_sqlite" ? "positive" : "neutral"}>{selectedPackageSummary?.dataPlane?.replaceAll("_", " ") ?? "Pending"}</Status>} /><dl className={ui("facts")}><div><dt>Namespace</dt><dd>{activeApp.id}</dd></div><div><dt>Access</dt><dd>Typed get / put</dd></div><div><dt>Raw SQLite</dt><dd>Kernel only</dd></div></dl></Card>
                      <Card title="Trial data" eyebrow="Runs beside Live"><p className={ui("body-copy")}>Each candidate gets an isolated branch for this App. Trial writes cannot change Live.</p><Status tone={candidateOpen ? "warning" : "neutral"}>{candidateOpen ? "Isolated branch available" : "No open Trial"}</Status></Card>
                      <Card title="Framework schemas" eyebrow="Signed package"><CodeResult value={selectedPackageRef?.manifest.schemas.map((schema) => ({ id: schema.id, version: schema.version, digest: schema.digest })) ?? []} /></Card>
                    </div>}
                  </div> : null}
                  {view === "versions" ? <div className={ui("data-contract-grid", "versions-grid")} data-scroll-owner="app-versions">
                    <Card title="Draft" eyebrow="Workbench source"><ListRow icon="code" title={`Source revision ${activeApp.source?.revision ?? "—"}`} description={activeApp.source?.dirty ? "Changed since exact validation" : draftValidationLabel} meta={<Status tone={draftValidationPassed ? "positive" : "warning"}>{activeApp.source?.dirty ? "Dirty" : "Saved"}</Status>} /><CodeResult value={activeApp.source ? { contentTag: activeApp.source.contentTag, sourceDigest: activeApp.source.digest, framework: selectedPackageDefinition?.framework } : null} /></Card>
                    <Card title="Trial" eyebrow="Runs beside Live">{candidateOpen && trialRelease ? <><ListRow icon="workbench" title={`${trialPackage?.title} ${trialPackage?.version}`} description="Exact candidate release · Live unchanged" meta={<Status tone="warning">Running</Status>} /><CodeResult value={{ candidateId: candidateQuery.data?.id, sourceDigest: trialRelease.sourceDigest, packageDigest: trialRelease.packageDigest }} /></> : <EmptyState title="No open Trial">Create a Trial from the current valid source.</EmptyState>}</Card>
                    <Card title="Live" eyebrow="Active composition">{liveRelease ? <><ListRow icon="check" title={`${livePackage?.title} ${livePackage?.version}`} description="Current registered App destination" meta={<Status tone="positive">Healthy</Status>} /><CodeResult value={{ sourceDigest: liveRelease.sourceDigest, packageDigest: liveRelease.packageDigest, revision: liveRelease.revision }} /></> : <EmptyState title="Not released">Live will appear after the exact Trial is approved and activated.</EmptyState>}</Card>
                    <Card title="Previous" eyebrow="Kernel rollback">
                      <p className={ui("body-copy")}>Rollback is Space-wide: the kernel previews the exact previous composition and blocks incompatible data changes before anything moves.</p>
                      {composition.data?.previous
                        ? <><ListRow icon="history" title={`Composition ${composition.data.previous.ordinal}`} description={composition.data.previous.manifest.appReleases.some((release) => release.appId === activeApp.id) ? "Contains the previous released version of this App" : "Predates this App release"} meta={<code>{composition.data.previous.digest.slice(0, 12)}</code>} /><Button variant="secondary" isDisabled={rollbackPreview.isPending} onPress={() => rollbackPreview.mutate()}>Check rollback</Button></>
                        : <Status tone="neutral">No previous composition</Status>}
                      {rollbackPreview.data ? <div className={ui(rollbackPreview.data.compatible ? "recovery-preview" : "danger-panel")}><strong>{rollbackPreview.data.compatible ? "Compatible rollback" : "Rollback blocked"}</strong><p>{rollbackPreview.data.explanation}</p><Button isDisabled={!rollbackPreview.data.compatible || rollback.isPending} onPress={() => rollback.mutate()}>Roll back exact composition</Button></div> : null}
                      <OperationError error={rollbackPreview.error ?? rollback.error} />
                    </Card>
                  </div> : null}
                  <OperationError error={operationError ?? appsQuery.error ?? appQuery.error ?? composition.error ?? candidateQuery.error} />
                </main>
                <PaneResizer className={ui("app-snowie-resizer")} defaultSize={400} label="Resize Snowie pane" maximum={560} minimum={340} onChange={setSnowieWidth} resize="after" value={snowieWidth} />
                <aside className={ui("app-editor-snowie")} aria-label="Snowie App Engineer">
                  <header><span><Icon name="snowie" /></span><div><strong>Snowie App Engineer</strong><small>Working on {activeApp.name}</small></div></header>
                  <PokeWorkspace key={`app:${activeApp.id}`} embedded spaceId={spaceId} requestedTarget={{ kind: "app", id: activeApp.id }} requestedTargetTitle={activeApp.name} />
                </aside>
              </div>
            </>
          : <EmptyState icon="workbench" title="Choose an App">Select an App or create one from a name and purpose.</EmptyState>}
    </section>
  </div>;
}
